import { useState, useEffect, useRef } from 'react';

import { captureAppException } from '@utils/exception-logger';

/**
 * @description A custom hook to handle global errors by capturing them with Sentry.
 * It ensures that each error is only captured once, even if the component re-renders.
 * @param error The error object to be handled
 * @returns { eventId: string } The Sentry event ID if the error was captured
 */
export const useGlobalErrorHandler = (error: Error & { digest?: string }) => {
  const [eventId, setEventId] = useState<string>('');
  const capturedRef = useRef(false);

  useEffect(() => {
    if (!error || capturedRef.current) return;
    capturedRef.current = true;

    try {
      const id = captureAppException(error, 'GlobalErrorBoundary', {
        digest: error.digest,
        pathname:
          typeof window !== 'undefined'
            ? window?.location?.pathname
            : undefined,
        userAgent:
          typeof navigator !== 'undefined' ? navigator?.userAgent : undefined,
        timestamp: Date.now(),
      });
      if (id) setEventId(id);
    } catch (e) {
      console.error('Global error capture failed', e);
    }
  }, [error]);

  return { eventId };
};
